import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const COLORS = ['#085041', '#0F6E56', '#5DCAA5', '#EAF6F1', '#D4EDE6', '#7C3AED', '#F59E0B'];

export default function AnalyticsDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('today');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const navigate = useNavigate();

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/stats`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Unable to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchStats, 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-tealLight/30 to-paper">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-inkSoft">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-tealLight/30 to-paper p-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-ink">Access Restricted</h2>
          <p className="text-inkSoft mt-2">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-6 py-2 bg-teal text-white rounded-lg hover:bg-teal/90 transition"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const countryData = stats?.countries?.map(c => ({
    name: c.country,
    value: c.count
  })) || [];

  const browserData = stats?.browsers?.map(b => ({
    name: b.browser,
    value: b.count
  })) || [];

  const deviceData = stats?.devices?.map(d => ({
    name: d.device,
    value: d.count
  })) || [];

  const pageData = stats?.top_pages?.map(p => ({
    name: p.page || '/',
    views: p.views
  })) || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-tealLight/30 to-paper text-ink antialiased">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <div className="inline-block mb-2">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tealLight border border-teal/20 text-teal text-xs font-semibold uppercase tracking-wider">
                📊 Analytics
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-teal tracking-tight">
              Visitor Analytics
            </h1>
            <p className="text-inkSoft mt-1">
              Real-time visitor tracking and insights
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={fetchStats}
              className="px-4 py-2 bg-teal text-white rounded-lg hover:bg-teal/90 transition text-sm font-medium"
            >
              🔄 Refresh
            </button>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 rounded-lg transition text-sm font-medium ${
                autoRefresh 
                  ? 'bg-green-100 text-green-700 border border-green-300' 
                  : 'bg-gray-100 text-gray-500 border border-gray-300'
              }`}
            >
              {autoRefresh ? '⏱️ Auto-refresh ON' : '⏱️ Auto-refresh OFF'}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 shadow-sm border border-rule hover:shadow-md transition">
            <div className="text-3xl font-bold text-teal">{stats?.today_visitors || 0}</div>
            <div className="text-sm text-inkSoft">👥 Visitors Today</div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border border-rule hover:shadow-md transition">
            <div className="text-3xl font-bold text-teal">{stats?.total_visitors || 0}</div>
            <div className="text-sm text-inkSoft">👤 Total Visitors</div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border border-rule hover:shadow-md transition">
            <div className="text-3xl font-bold text-teal">{stats?.total_sessions || 0}</div>
            <div className="text-sm text-inkSoft">📋 Sessions</div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border border-rule hover:shadow-md transition">
            <div className="text-3xl font-bold text-teal">{stats?.total_page_views || 0}</div>
            <div className="text-sm text-inkSoft">📄 Page Views</div>
          </div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Countries Chart */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-rule">
            <h3 className="text-lg font-bold text-ink mb-4">🌍 Visitors by Country</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={countryData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAF6F1" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={80} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#085041" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Browsers Pie Chart */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-rule">
            <h3 className="text-lg font-bold text-ink mb-4">🌐 Browsers</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={browserData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {browserData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Devices Pie Chart */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-rule">
            <h3 className="text-lg font-bold text-ink mb-4">📱 Devices</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={deviceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {deviceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Pages Chart */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-rule">
            <h3 className="text-lg font-bold text-ink mb-4">📄 Top Pages</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pageData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAF6F1" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="views" fill="#0F6E56" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Recent Visitors Table */}
        <div className="bg-white rounded-xl shadow-sm border border-rule overflow-hidden">
          <div className="px-6 py-4 border-b border-rule flex justify-between items-center">
            <h3 className="text-lg font-bold text-ink">🕐 Recent Visitors</h3>
            <span className="text-xs text-inkSoft">{stats?.recent_visitors?.length || 0} visitors</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-tealLight/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-inkSoft uppercase tracking-wider">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-inkSoft uppercase tracking-wider">Page</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-inkSoft uppercase tracking-wider">Country</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-inkSoft uppercase tracking-wider">City</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-inkSoft uppercase tracking-wider">Browser</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-inkSoft uppercase tracking-wider">Device</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule">
                {stats?.recent_visitors?.length > 0 ? (
                  stats.recent_visitors.slice(0, 20).map((visitor, index) => (
                    <tr key={index} className="hover:bg-tealLight/10 transition">
                      <td className="px-6 py-3 text-sm text-inkSoft whitespace-nowrap">{visitor.time}</td>
                      <td className="px-6 py-3 text-sm text-ink font-medium">{visitor.page}</td>
                      <td className="px-6 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          visitor.country === 'India' ? 'bg-orange-100 text-orange-700' :
                          visitor.country === 'United States' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {visitor.country}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-inkSoft">{visitor.city || 'N/A'}</td>
                      <td className="px-6 py-3 text-sm text-inkSoft">{visitor.browser}</td>
                      <td className="px-6 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          visitor.device === 'Mobile' ? 'bg-purple-100 text-purple-700' :
                          visitor.device === 'Desktop' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {visitor.device}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-inkSoft">
                      No visitor data available yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-inkSoft/60">
          <p>Data updated in real-time · Auto-refresh {autoRefresh ? 'ON' : 'OFF'}</p>
          <p className="mt-1">⚠️ Demonstration only. Not intended for clinical diagnosis or treatment.</p>
        </div>
      </div>
    </div>
  );
}
